<template>
  <div v-if="!loading && page && page.template">
    <component
      :is="TemplateComponents[page.template]"
      v-bind="{ page }"
      :is-mobile="uiStore.isMobile"
      :is-scrolled="isScrolled"
      :has-site-wide-message="hasSiteWideMessage"
    />
    <SellerDialog
      v-if="uiStore.showSellerDialog"
    />
  </div>
</template>

<script setup lang="ts">
import { type Head } from '@unhead/schema';
import { useConfigStore } from '~/store/config';
import type Page from '~/models/page';
import TemplateComponents from '~/constants/template-components';
import { useUiStore } from '~/store/ui';
import SellerDialog from '~/components/seller/SellerDialog.vue';

const config = useConfigStore();
const { apiGet } = useApiFetch();
const uiStore = useUiStore();
const imgScaler = useImage();
const route = useRoute();
const { $lang } = useNuxtApp();
const url = useRequestURL().pathname;

const page = ref<Page | null>(null);
const loading = ref(true);

const isScrolled = ref(false);
const head = {} as Head;

const splitRoute = route.path.split('/');

let fixedRoute = route.path;
if (route.path === '' || route.path === '/') {
  fixedRoute = '/se/';
} else if (splitRoute[2] === 'group' || splitRoute[2] === 'seller') {
  fixedRoute = `/${splitRoute[1]}/`;
}

const throwHard404 = () => {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not found',
  });
};

let routeId = config.getRouteIdFromPath(fixedRoute);
let isFrontPage = false;
if (routeId === -1) {
  setResponseStatus(404);
  console.warn('Couldn\'t lookup route id');
  // trying to find the 404 page
  routeId = config.pageByTemplate('template-404')?.id || -1;

  if (routeId === -1) {
    console.warn('Couldn\'t find 404 page');
    throwHard404();
  }
} else {
  const fullRouteData = config.pageById(routeId);
  if (fullRouteData?.is_startpage) {
    isFrontPage = true;
  }
}

const { data } = await useAsyncData('pageContentLoad', async() => {
  let queryArr = [];
  let preview = '';
  if (route.query?.ge_preview_nonce) {
    queryArr.push(`ge_preview_nonce=${route.query.ge_preview_nonce}` as string);
    preview = '-preview' as string;
  }

  const queryParam = queryArr.length ? '?' + queryArr.join('&') : '';
  const res =  await apiGet<Page>(`/wp-json/api/v1/post-data${preview}/${routeId}${queryParam}`);
  return {
    page: res,
  };
});

if (data.value?.page) {
  page.value = data.value.page;
  head.title = page.value.title;
  loading.value = false;

  const ogTitle = page.value.seo?.og_title || '';
  let ogImage = '';

  if (page.value.seo?.og_image?.url) {
    if (typeof page.value.seo.og_image.url === 'string') {
      ogImage = page.value.seo.og_image.url;
    } else {
      ogImage = page.value.seo.og_image.url[0];
    }
  }

  let ogDescription = page.value.seo?.og_description || '';

  /**
   * We only change description and image if ?sellerId is in url
   * otherwise, what's actually shared doesn't match og tags
   */
  
  if (route.query?.sellerid || url.includes('seller')) {
    if (config.clubCookie.Name && config.sellerCookie.Name) {
      ogDescription = $lang('header', 'club_site_message_long', {
        club: config.clubCookie.Name,
        seller: config.sellerCookie.Name,
      });
      if (config.clubCookie.Picture && isFrontPage) {
        ogImage = config.clubCookie.Picture;
      }
    }
  }

  if (ogImage !== '') {
    if (ogImage.includes('https://')) {
      //ogImage = useRequestURL().origin + imgScaler(ogImage, { width: 1200 });
    } else {
      ogImage = useRequestURL().origin + imgScaler(ogImage, { width: 1200 });
    }
  }

  useHead({
    ...head,
    titleTemplate: (titleChunk) => {
      return titleChunk ? `${titleChunk} - ${config.pageName}` : config.pageName;
    },
    htmlAttrs: {
      lang: `${config.market.language}`,
    },
  });
  useSeoMeta({
    ogTitle,
    ogDescription,
    ogImage,
  });
} else {
  throwHard404();
}

const hasSiteWideMessage = computed<boolean>(()=> {
  return !!config.siteWideMessage;
});

</script>

<style scoped lang="postcss">
</style>
